import { useEffect } from 'react';

import { TEMPLATES_PATH } from 'routes/routes.constants';

import { useRouter } from '@polyai/common/hooks/useRouter';
import { Loader } from '@polyai/ui/components/atoms/Loader';

import { useBranchQueryParams } from 'hooks/useBranchQueryParams';
import useFeatureSwitch from 'hooks/useFeatureSwitch/useFeatureSwitch';
import { useIsPlgProd } from 'hooks/useIsPlgProd';
import useProjectInit from 'hooks/useProjectInit';
import { useProjectResources } from 'hooks/useProjectResources';
import useProjectRoute from 'hooks/useProjectRoute';
import useSampleProject from 'hooks/useSampleProjects/useSampleProject';
import { useTemplateEngine } from 'hooks/useTemplateEngine';
import { generateProjectLandingResource } from 'lib/routeHelper';
import ErrorPage, { ErrorStatus } from 'screens/ErrorPage';

const ProjectRedirect = () => {
  const { project, isLoading, isNotFound, isError } = useProjectInit();
  const { isFetchingProjectFeatures } = useProjectResources();
  const { replaceUrl } = useRouter();
  const { inSampleProject } = useSampleProject();
  const { isFeatureOn } = useFeatureSwitch();
  const { isTemplateEngineEnabled } = useTemplateEngine();
  const { accountId, projectId } = useProjectRoute();
  const { branchId } = useBranchQueryParams();
  const isPlgProd = useIsPlgProd();

  useEffect(() => {
    if (inSampleProject && !isLoading) {
      replaceUrl(`${TEMPLATES_PATH}/${projectId}/agent`);
      return;
    }

    if (!isFetchingProjectFeatures && !isLoading && !isError && project) {
      const redirectUser = async () => {
        const path = await generateProjectLandingResource(
          accountId,
          projectId,
          project.type,
          inSampleProject,
          isFeatureOn,
          branchId ?? undefined,
          isTemplateEngineEnabled,
          isPlgProd,
        );
        replaceUrl(path);
      };
      void redirectUser();
    }
  }, [
    accountId,
    projectId,
    isFetchingProjectFeatures,
    isLoading,
    isError,
    replaceUrl,
    project?.type,
    project,
    inSampleProject,
    isFeatureOn,
    isTemplateEngineEnabled,
    branchId,
    isPlgProd,
  ]);

  if (isFetchingProjectFeatures || isLoading) {
    return <Loader inProjectPage={false} fullScreen={true} logo={true} />;
  }

  if (isNotFound) {
    return <ErrorPage type={ErrorStatus.NOT_FOUND} />;
  }

  if (isError) {
    throw new Error('Failed to load project data');
  }

  return <Loader fullScreen={true} logo={true} />;
};

export default ProjectRedirect;
